import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { getCourseEnrollmentReport } from "action/report";
import { courseEnrollmentReport } from "constants/report";

function CourseEnrollmentReport() {
  const initialFilters = {
    course: "",
    batch: "",
  };

  const dispatch = useDispatch();

  const courseEnrollmentReportData = useSelector(
    (state) => state.report.courseEnrollmentReport,
  );

  const [filters, setFilters] = useState(initialFilters);

  const [filterState, setFilterState] = useState({
    filterParams: [],
  });

  const [pageNum, setPageNum] = useState(1);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getCourseEnrollmentReport(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({
        type: courseEnrollmentReport,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (courseEnrollmentReportData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          courseEnrollmentReportData?.data?.courseEnrollmentReport?.length ===
          0,
        showLoaderOnClick: false,
      });
    } else if (
      courseEnrollmentReportData?.code ||
      courseEnrollmentReportData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          courseEnrollmentReportData?.message ||
          "Failed to load course enrollment report.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [courseEnrollmentReportData]);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.course?.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filters.course.trim()],
      });
    }

    if (filters.batch?.trim()) {
      filterParams.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filters.batch.trim()],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((previous) => ({
        ...previous,
        isLoading: true,
        error: "",
        showLoaderOnClick: true,
      }));

      setFilterState({
        filterParams: newFilterParams,
      });

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((previous) => ({
      ...previous,
      isLoading: true,
      error: "",
      showLoaderOnClick: false,
    }));

    setFilters(initialFilters);

    setFilterState({
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Course",
      accessor: "course",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Batch",
      accessor: "batch",
      minWidth: 200,
      maxWidth: 260,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Trainee Count",
      accessor: "traineeCount",
      minWidth: 120,
      maxWidth: 140,
      centerAlign: true,
    },
    {
      Header: "Available Capacity",
      accessor: "availableCapacity",
      minWidth: 150,
      maxWidth: 170,
      centerAlign: true,
    },
    {
      Header: "Enrollment Date",
      accessor: "enrollmentDate",
      minWidth: 150,
      maxWidth: 170,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
  ];

  const tableData = (
    courseEnrollmentReportData?.data?.courseEnrollmentReport || []
  ).map((item, index) => ({
    ...item,
    id: `${item?.courseID}-${item?.batchID}-${item?.enrollmentDate}-${index}`,
    srNo:
      (pageNum - 1) *
        (courseEnrollmentReportData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    course: item?.courseName || "-",
    batch: item?.batchName || "-",
    traineeCount: item?.traineeCount ?? 0,
    availableCapacity: item?.availableCapacity ?? 0,
    enrollmentDate: item?.enrollmentDate || "-",
  }));

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Course Enrollment Report
                </CardTitle>
              </CardHeader>

              {/* Body */}
              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Record Count */}
                <div className="mt-2 mb-3 mx-1">
                  {courseEnrollmentReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {courseEnrollmentReportData?.data
                            ?.totalFilteredRecords || 0}{" "}
                          of{" "}
                          {courseEnrollmentReportData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {courseEnrollmentReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table States */}
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No enrollment records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData}
                      defaultPageSize={
                        courseEnrollmentReportData?.data
                          ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (courseEnrollmentReportData?.data
                          ?.totalFilteredRecords || 0) /
                          (courseEnrollmentReportData?.data
                            ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE),
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CourseEnrollmentReport;
